import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { sharedConstants } from 'src/environments/constants';
import { MapOptionsShared } from '../../../models/interface/mapOptions.interface';
import * as mapStyle from '../../../../../../assets/mapStyle/mapStyle.json';
import { CoordinateShared } from '../../../models/interface/coordinate.interface';
import { MapElementShared } from '../../../models/interface/mapElement.interface';
import { MuMapMarkerService } from '../../../sharedServices/map/mu-map-marker.service';
import { MarkerType } from '../../../models/interface/markerType.enum';
import { PolygonDataShared } from '../../../models/interface/polygonData.interface';
import { MarkerConfigShared } from '../../../models/interface/markerConfig.interface';
import { MuMapAtlasService } from '../mu-atlas/services/mu-map-atlas.service';
import { AtlasMarker } from '../mu-atlas/interfaces/mu-atlas-marker.interface';
import { AtlasPosition, AtlasType } from '../mu-atlas/interfaces/mu-atlas-types.interface';
import { AtlasPolygon } from '../mu-atlas/interfaces/mu-atlas-polygon.interface';
import { sharedDeliveryConstants } from '../../../sharedConstants/delivery.constants';

@Component({
  selector: 'app-mu-map',
  templateUrl: './mu-map.component.html',
  styleUrls: ['./mu-map.component.sass'],
})
export class MuMapComponent<ActionType> implements OnInit, AfterViewInit {
  @Input() mapOptions: MapOptionsShared;
  @Input() markers: MapElementShared[] = [];
  @Input() class?: string;
  @Input() mapType?: string = AtlasType.LEAFLET;
  @Output() eventSetMap = new EventEmitter<any>();
  @Output() eventComponentReady = new EventEmitter<MuMapComponent<ActionType>>();
  map: any;
  baseMapOptions: MapOptionsShared;

  constructor(
    private muMapMarkerService: MuMapMarkerService,
    private cd: ChangeDetectorRef,
    private muMapAtlasService: MuMapAtlasService
  ) {
    /** */
  }

  ngOnInit(): void {
    this.baseMapOptions = sharedDeliveryConstants.mapOptions;
// eslint-disable-next-line @typescript-eslint/dot-notation
    /*eslint-disable-next-line @typescript-eslint/dot-notation*/
    this.baseMapOptions.styles = mapStyle['default'];
  }

  ngAfterViewInit() {
    this.cd.detectChanges();
  }

  /**
   * emit get map
   * @param $event
   */
  _eventSetMap($event) {
    this.map = $event;
    this.updateMapOptions(this.mapOptions?.styles ? this.mapOptions : this.baseMapOptions);
    this.eventSetMap.emit(this.map);
    this.eventComponentReady.emit(this);
  }

  /**
   * update map options
   * @param mapOptions
   */
  updateMapOptions(mapOptions: MapOptionsShared): void {
    this.mapOptions.notFitAndZoom = mapOptions.notFitAndZoom;
    if (this.map && this.map.updateOptions) {
      this.map.updateOptions(mapOptions);
    }
  }

  /**
   * Validate last marker draggable and return with default position
   * @param markers Markers to check
   * @return [ MapElement[] ] markers
   * @return [ boolean ] Special case If is first element
   */
  validateLastMarker(markers: MapElementShared[]): [MapElementShared[], boolean] {
    let firstElement = false;
    try {
      const positionMarker = markers[markers.length - 1] as AtlasMarker;
      if (positionMarker && positionMarker?.getPosition() === undefined) {
        if (markers.length === 1) {
          firstElement = true;
          positionMarker.setPosition(this.map.getCenter());
        } else {
          const nearPosition = this.muMapAtlasService
            .createBounds(this.map.getCenter())
            .getCenter();
          positionMarker.setPosition({
            lat: nearPosition.lat + 0.01,
            lng: nearPosition.lng + 0.01,
          });
        }
        markers[markers.length - 1] = positionMarker;
      }
// eslint-disable-next-line no-empty
    } catch (e) {}

    return [markers, firstElement];
  }

  /**
   * update markers and set map bounds
   * @param markers
   */
  updateMarkers(markers: MapElementShared[]) {
    const markersValidate = this.validateLastMarker(markers);
    this.markers = markersValidate[0];
    if (
      !markersValidate[1] &&
      this.map &&
      this.markers?.length > 0 &&
      !this.mapOptions.notFitAndZoom
    ) {
      this.fitBounds();
      if (this.markers.length === 1) {
        this.map.setZoom(12);
      }
    }
  }

  /**
   * Replace the marker collection without validation or viewport changes.
   */
  updateMarkersPositionOnly(markers: MapElementShared[]): void {
    this.markers = [...markers];
  }

  /**
   * fit bounds
   * @param {boolean} autoZoom set a default zoom
   * @param {number} padding set a padding after bounds
   */
  fitBounds(autoZoom?: boolean, padding = 100, zoomValue?: number): void {
    const bounds = this.muMapAtlasService.createBounds();
    for (const marker of this.markers) {
      const tmpMarker = marker as AtlasMarker;
      if (typeof tmpMarker?.getPosition === 'function') {
        bounds.extend(tmpMarker?.getPosition());
      }
    }
    this.map?.fitBounds(bounds, padding);
    if (this.map.panToBounds) {
      this.map.panToBounds(bounds);
    }
    if (zoomValue) {
      this.map.setZoom(zoomValue);
    }
    if (autoZoom) {
      this.map.setZoom(this.map.getZoom() - 1);
    }
  }

  /**
   * clear markers
   * @param markers
   */
  removeMarkers() {
    this.markers = [];
  }

  /**
   * create polygon
   * @param {PolygonDataShared} data
   */
  async createPolygon(data: PolygonDataShared): Promise<void> {
    const markerConfig: MarkerConfigShared = data.marker?.get('markerConfig');
    data.coordinates = {
      latitude: markerConfig?.mapAddress?.coordinates?.latitude || data.coordinates?.latitude,
      longitude: markerConfig?.mapAddress?.coordinates?.longitude || data.coordinates?.longitude,
    };
    this.createPolygonFit(data);
    this.createPolygonBuildMarker(data);
    const tmpMarkers = this.markers.filter(x => !(x as AtlasPolygon)?.getPath);
    this.markers =
      !this.markers || this.markers.length === 0 ? [data.polygon] : [...tmpMarkers, data.polygon];
  }

  /**
   * fit bounds on creat epolygon
   * @param data
   */
  createPolygonFit(data: PolygonDataShared): void {
    const index = Math.trunc(data.polygon.getPath().length / 2);
    const coordinate: CoordinateShared = {
      latitude: data.polygon.getPath()[index].lat,
      longitude: data.polygon.getPath()[index].lng,
    };
    if (this.markers?.length > 0) {
      const bounds = this.muMapAtlasService.createBounds();
      for (const marker of this.markers) {
        const mark = marker as AtlasMarker;
        if (typeof mark?.getPosition === 'function') {
          bounds.extend(mark?.getPosition());
        }
      }
      const boundsNew: any = bounds;
      if (!boundsNew.coordinates) {
        return;
      }
      this.map.fitBounds(bounds, 100);
    } else {
      const bounds = { lat: coordinate.latitude, lng: coordinate.longitude } as AtlasPosition;
      const limits = this.muMapAtlasService.createBounds();
      limits.extend(bounds);
      this.map.fitBounds(limits);
      this.map.setCenter(bounds);
      if (data.zoom) {
        this.map.setZoom(data.zoom);
      }
    }
  }

  /**
   * build marker on create polygons
   */
  createPolygonBuildMarker(data: PolygonDataShared): void {
    if (data.coordinates?.latitude && data.coordinates?.longitude) {
      const position = {
        lat: data.coordinates.latitude,
        lng: data.coordinates.longitude,
      } as AtlasPosition;
      const coverageMarker = this.markers.find(x => {
        const mPos = this.muMapAtlasService
          .createBounds((x as AtlasMarker).getPosition())
          .getCenter();
        return mPos.lat === position.lat && mPos.lng === position.lng;
      });
      if (!data.marker) {
        const localMarkerConfig: MarkerConfigShared = {
          type: MarkerType.COVERAGE,
        };
        coverageMarker.setValues({ markerConfig: localMarkerConfig });
      }
      this.muMapMarkerService.buildMarker(data.marker || coverageMarker);
    }
  }

  /**
   * focus map on a Lat, Lng
   * @param {any} marker
   * @param {number} zoom
   */
  async focusOn(polygon: any, zoom?: number): Promise<void> {
    const bounds = this.muMapAtlasService.createBounds();
    for (const latLng of polygon.getPath()) {
      bounds.extend(latLng);
    }
    this.map.fitBounds(bounds, 100);
    if (zoom) {
      this.map.setZoom(zoom);
    }
  }

  /**
   * fit bounds with auto zoom
   * @param {number} padding
   */
  cleanFitBounds(padding = 20): void {
    const bounds = this.muMapAtlasService.createBounds();
    if (this.markers.filter(m => m.get('markerConfig')).length > 1) {
      for (const marker of this.markers) {
        const tmpMarker = marker as AtlasMarker;
        if (typeof tmpMarker?.getPosition === 'function') {
          bounds.extend(tmpMarker?.getPosition());
        }
      }
      this.map.fitBounds(bounds, padding);
    }
  }
}
